import { useEffect, useState, useCallback } from "react";

/**
 * usePolicyVersions
 * ----------------------------------------------------------------------------
 * Fetches the canonical published version of each legal document from the
 * website's static manifest (`/policy-versions.json`), generated at build
 * time from the front-matter of the policy markdown files.
 *
 * This replaces hard-coded "v1" consent version stamps with the real value
 * the website publishes, closing the loop between what users see on the
 * site and what the app records against their consent.
 *
 * Manifest shape (served from the website `public/` dir):
 *   {
 *     "generatedAt": "...",
 *     "privacyPolicy":      { "version": "1", "effectiveDate": "2025-08-03" },
 *     "termsAndConditions": { "version": "1", "effectiveDate": "2025-08-03" }
 *   }
 */

const POLICY_VERSIONS_URL =
  "https://toothfairyai.com/policy-versions.json";

const FETCH_TIMEOUT_MS = 4000;

const usePolicyVersions = () => {
  const [manifest, setManifest] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchVersions = useCallback(async () => {
    setIsLoading(true);
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      const res = await fetch(POLICY_VERSIONS_URL, {
        signal: controller.signal,
        cache: "no-store",
      });
      clearTimeout(timeout);
      if (!res.ok) throw new Error(`policy-versions HTTP ${res.status}`);
      const data = await res.json();
      setManifest(data);
      setError(null);
      return data;
    } catch (err) {
      // Never crash the app on a policy-versions fetch failure; callers fall
      // back to the hard-coded Constants.newsletterConsent.termsVersion.
      console.warn("usePolicyVersions: fetch failed", err?.message);
      setError(err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  return {
    fetchVersions,
    isLoading,
    error,
    manifest,
    currentPrivacyVersion: manifest?.privacyPolicy?.version || null,
    currentPrivacyEffectiveDate: manifest?.privacyPolicy?.effectiveDate || null,
    currentTermsVersion: manifest?.termsAndConditions?.version || null,
  };
};

export default usePolicyVersions;